import type { MouseEvent as ReactMouseEvent } from 'react';

/**
 * Pushes a custom event into Google Tag Manager's dataLayer (GTM-TGKLNJS,
 * loaded in index.html). GTM owns forwarding events to GA4 via its own
 * "Google Tag" — pushing here alone does nothing in GA4 until a matching
 * Custom Event trigger + GA4 Event tag exists in the GTM container reading
 * this same event name and parameters, same as the existing site_search event.
 */
export function pushDataLayerEvent(eventName: string, params: Record<string, unknown> = {}) {
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({ event: eventName, ...params });
}

/**
 * Fires Meta's real `Lead` standard event + GA4's `generate_lead` — call
 * this ONLY after a backend-confirmed successful inquiry/booking/
 * registration/contact-form submit (never from a mere click or a form just
 * opening). Before 2026-08-29 this was ALSO called from every Call/
 * WhatsApp/Email button click, so Meta Test Events and GA4 showed nothing
 * but undifferentiated "Lead"s for every kind of engagement (confirmed
 * live via Test Events screenshots) — those now use the distinct
 * trackViewProfile/trackClickCall/trackClickWhatsApp/trackClickEmail/
 * trackInquiryFormOpen functions below instead, so this name means what it
 * says. `window.fbq` is a no-op (optional chaining) if no Pixel ID is
 * configured in Site Settings, so this is safe to call unconditionally.
 */
export function trackLeadConversion(leadType: string, extraParams: Record<string, unknown> = {}) {
  pushDataLayerEvent('generate_lead', { lead_type: leadType, ...extraParams });
  (window as any).fbq?.('track', 'Lead', { content_name: leadType });
}

/**
 * Pre-conversion engagement signals — distinct from trackLeadConversion()
 * above on purpose, so Meta Test Events / GA4 can finally tell "viewed a
 * profile" apart from "clicked Call" apart from an actual completed Lead,
 * instead of everything reporting as the same generic event (confirmed
 * live, 2026-08-29). Sent as a Meta Custom Event (fbq trackCustom, not
 * fbq track — these aren't Meta's own standard event types) plus a
 * matching dataLayer push.
 *
 * dataLayerEventName reaching GA4 as an actual GA4 event still needs a
 * Custom Event trigger + GA4 Event tag added in the GTM container for that
 * name (same as every other custom dataLayer event on this site, e.g.
 * site_search) — that's a GTM console change, not something this function
 * can do; pushDataLayerEvent()'s own doc comment covers why.
 */
function trackEngagementEvent(
  fbEventName: 'ViewProfile' | 'ClickCall' | 'ClickWhatsApp' | 'ClickEmail' | 'InquiryFormOpen',
  dataLayerEventName: string,
  params: Record<string, unknown> = {}
) {
  pushDataLayerEvent(dataLayerEventName, params);
  (window as any).fbq?.('trackCustom', fbEventName, params);
}

/** Fire once when a provider/doctor profile page is actually viewed (not on every re-render). */
export function trackViewProfile(params: Record<string, unknown> = {}) {
  trackEngagementEvent('ViewProfile', 'view_profile', params);
}

/** Fire when a Call button is clicked — NOT a Lead (no submission happened yet). */
export function trackClickCall(params: Record<string, unknown> = {}) {
  trackEngagementEvent('ClickCall', 'click_call', params);
}

/** Fire when a WhatsApp button is clicked — NOT a Lead. */
export function trackClickWhatsApp(params: Record<string, unknown> = {}) {
  trackEngagementEvent('ClickWhatsApp', 'click_whatsapp', params);
}

/** Fire when an Email/contact button is clicked — NOT a Lead. */
export function trackClickEmail(params: Record<string, unknown> = {}) {
  trackEngagementEvent('ClickEmail', 'click_email', params);
}

/** Fire when an inquiry/booking form actually opens — NOT a Lead (nothing submitted yet). */
export function trackInquiryFormOpen(params: Record<string, unknown> = {}) {
  trackEngagementEvent('InquiryFormOpen', 'inquiry_form_open', params);
}

/**
 * Click-delegation handler for admin-editable CMS content rendered via
 * dangerouslySetInnerHTML (About/Contact/Terms/Privacy/etc. page bodies) —
 * a plain React onClick can't be attached to individual links inside raw
 * HTML, but one delegated listener on the containing element can still
 * inspect e.target. Found live during the tracking-funnel audit
 * (2026-08-29): the /contact page alone had 4 extra mailto: links (support/
 * refunds/privacy/grievance@patakare.com) embedded in its CMS body, all
 * completely untracked, distinct from the 2 hardcoded JSX contact links
 * already covered separately. Attach as `onClick={(e) => handleContactLinkClick(e, 'about_page_content')}`
 * on the dangerouslySetInnerHTML container.
 */
export function handleContactLinkClick(e: ReactMouseEvent, source: string) {
  const link = (e.target as HTMLElement)?.closest?.('a');
  const href = link?.getAttribute('href') || '';
  if (href.startsWith('tel:')) trackClickCall({ source });
  else if (href.startsWith('mailto:')) trackClickEmail({ source });
  else if (href.startsWith('https://wa.me')) trackClickWhatsApp({ source });
}
